import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Paper,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";

import { type CsvData } from "../CsvDataTable/useCsvData";

/**
 * FileUploadSection Props
 */
interface FileUploadSectionProps {
  csvFile: File | null;
  csvData: CsvData[];
  isUploading: boolean;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileRemove: () => void;
}

/**
 * CSV 파일 업로드 섹션
 * @description CSV 파일 업로드 및 상태 표시 컴포넌트입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
export const FileUploadSection = ({
  csvFile,
  csvData,
  isUploading,
  onFileUpload,
  onFileRemove,
}: FileUploadSectionProps) => {
  return (
    <Paper elevation={0} variant="outlined">
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          📄 Step 1: CSV 파일 업로드
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Email, First name, Last name, Phone 컬럼이 포함된 CSV 파일을 업로드하세요.
          <br />
          <strong>필수:</strong> Email만 &nbsp;&nbsp; <strong>선택:</strong> First name, Last name, Phone
          <br />
          <strong>자동 처리:</strong> 중복 이메일 제거, First name 없으면 "Unknown" 설정
        </Typography>

        {!csvFile ? (
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              backgroundColor: "#fafafa",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <input
              accept=".csv"
              type="file"
              id="csv-upload"
              style={{ display: "none" }}
              onChange={onFileUpload}
              disabled={isUploading}
            />
            <label htmlFor="csv-upload">
              <Button
                variant={"outlined"}
                disableElevation
                component="span"
                startIcon={<UploadIcon />}
                disabled={isUploading}
                size="large"
              >
                {isUploading ? "업로드 중..." : "CSV 파일 선택"}
              </Button>
            </label>
            <Typography variant="caption" display="block" mt={1}>
              .csv 파일만 업로드 가능합니다.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                📁 <strong>{csvFile.name}</strong> 업로드 완료
                <br />총 {csvData.length}명의 멤버 데이터가 준비되었습니다.
              </Typography>
            </Alert>
            <Button
              variant={"text"}
              disableElevation
              color="error"
              onClick={onFileRemove}
              size="small"
            >
              파일 제거
            </Button>
          </Box>
        )}

        {isUploading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant="caption" mt={1}>
              파일을 분석 중입니다...
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
